import type { ShapeStyle } from "~/lib/svgEditor/lineWidth";
import type { OutlineWeight } from "~/lib/svgEditor/outline";
import type { Box } from "~/lib/svgEditor/selection";
import type { Point } from "~/lib/svgEditor/zoom";
import {
    IDENTITY,
    invert,
    type Matrix,
    matrixAttribute,
    multiply,
} from "~/lib/svgEditor/matrix";
import { figurePathData } from "~/lib/svgEditor/pathData";
import {
    bareGeometry,
    insetOf,
    SHAPE_SELECTOR,
    type ShapeEdit,
    type ShapeGeometry,
} from "~/lib/svgEditor/transform";

/**
 * What the document component exposes about its shapes: a shape is
 * identified by its index in document order.
 */
export type DocumentApi = {
    /** The preview is the document now: the shapes stay as they are, `svg` being what they show. */
    commitPreview: (svg: string) => void;
    discardPreview: () => void;
    previewAttributes: (edits: ReadonlyMap<number, ShapeEdit>) => void;
    /** The document `svg` was rendered as, sanitized — when it was `svg` that was rendered. */
    renderedOf: (svg: string) => string | undefined;
    shapeCount: () => number;
    shapeGeometry: (index: number) => ShapeGeometry | undefined;
    shapeIndexOf: (target: EventTarget | null) => number | undefined;
    /**
     * Whether some point along the shape's outline, in the document's
     * units and at most `spacing` apart, passes `test` — only those near
     * `within` are looked at. Undefined for a shape without an outline.
     */
    shapeOutlineSome: (
        index: number,
        spacing: number,
        within: Box | undefined,
        test: (point: Point) => boolean,
    ) => boolean | undefined;
    shapePath: (index: number) => ShapePath | undefined;
    shapeSilhouette: (index: number) => string | undefined;
    shapeStyle: (index: number) => ShapeStyle | undefined;
    shapeWeight: (index: number) => OutlineWeight;
};

/** A shape's geometry as path data in its own coordinates, and where that lies in the document. */
export type ShapePath = {
    data: string;
    /** What places the data in the document; none when it lies there as it is. */
    matrix: Matrix | undefined;
    /** The same as a `transform` attribute. */
    transform: string | undefined;
};

const IDENTITY_ATTRIBUTE = matrixAttribute(IDENTITY);
/** What a rect, circle or ellipse weighs, about the path data they come to. */
const FIGURE_WEIGHT = 60;

// Above this many shapes there is no delay per element any more — a very
// detailed vectorization would otherwise take minutes to come in.
const MAX_STAGGERED_SHAPES = 1500;
const TOTAL_STAGGER_MS = 1600;
const MAX_DELAY_STEP_MS = 45;

/**
 * The viewBox, which is the coordinate system the document is drawn in;
 * without one, the requested embedding size, if it is numeric.
 */
export function documentBoxOf(svg: SVGSVGElement): Box | undefined {
    const viewBox = svg.viewBox.baseVal;

    if (viewBox.width > 0 && viewBox.height > 0) {
        return {
            height: viewBox.height,
            width: viewBox.width,
            x: viewBox.x,
            y: viewBox.y,
        };
    }

    const width = Number.parseFloat(svg.getAttribute("width") ?? "");
    const height = Number.parseFloat(svg.getAttribute("height") ?? "");

    return width > 0 && height > 0 ? { height, width, x: 0, y: 0 } : undefined;
}

/**
 * Where `shape` lies, in the document's user units: its box with its
 * transforms applied (axis-aligned in the document), its parent's matrix
 * into the document, and its own transform.
 *
 * Via the screen matrices of shape and root: `getCTM()` would end in the
 * root's viewport pixels, viewBox scaling and centering included, not in its
 * user units.
 */
export function shapeGeometryOf(
    shape: Element | undefined,
    root: Element | null | undefined,
): ShapeGeometry | undefined {
    if (!isGraphics(shape) || !isGraphics(root)) return;

    const toScreen = shape.getScreenCTM();
    const rootToScreen = root.getScreenCTM();

    if (!toScreen || !rootToScreen) return;

    const toRoot = fromDom(rootToScreen.inverse().multiply(toScreen));
    const transform = ownTransform(shape);
    const toParent = invert(transform);

    if (!toParent) return;

    const local = shape.getBBox();
    const corners = [
        [local.x, local.y],
        [local.x + local.width, local.y],
        [local.x, local.y + local.height],
        [local.x + local.width, local.y + local.height],
    ].map(([x, y]) => ({
        x: toRoot.a * x + toRoot.c * y + toRoot.e,
        y: toRoot.b * x + toRoot.d * y + toRoot.f,
    }));
    const xs = corners.map((corner) => corner.x);
    const ys = corners.map((corner) => corner.y);
    const x = Math.min(...xs);
    const y = Math.min(...ys);

    return {
        box: {
            height: Math.max(...ys) - y,
            width: Math.max(...xs) - x,
            x,
            y,
        },
        parentToRoot: multiply(toRoot, toParent),
        transform,
    };
}

/** The index in `shapes` of the shape `target` is part of, if it is part of one. */
export function shapeIndexIn(
    shapes: readonly Element[],
    target: EventTarget | null,
): number | undefined {
    if (!(target instanceof Element)) return;

    const shape = target.closest(SHAPE_SELECTOR);

    if (!shape) return;

    // A shape inside a definition is never drawn, so never clicked; -1 then.
    const index = shapes.indexOf(shape);

    return index === -1 ? undefined : index;
}

/**
 * `shape`'s geometry as path data in its own coordinates — its path's data
 * as it is, or its figure as a path — and the transform that places it in
 * the document, or none when it lies there as it is. For whoever draws
 * along many shapes as a few paths, such as the plain outline: shapes with
 * the same transform join into one path, which costs no more than joining
 * their data. Undefined when the shape has no geometry to read.
 */
export function shapePathOf(
    shape: Element | undefined,
    root: Element | null | undefined,
    geometry = shapeGeometryOf(shape, root),
): ShapePath | undefined {
    if (!shape || !geometry) return;

    const data = figurePathData(shape.tagName.toLowerCase(), (name) =>
        shape.getAttribute(name),
    );

    if (data === undefined) return;

    const matrix = multiply(geometry.parentToRoot, geometry.transform);
    const transform = matrixAttribute(matrix);
    const placed = transform !== IDENTITY_ATTRIBUTE;

    return {
        // A path's first move is absolute by itself, but not any more once
        // the data follows another path's.
        data: data.replace(/^\s*m/, "M"),
        matrix: placed ? matrix : undefined,
        transform: placed ? transform : undefined,
    };
}

/**
 * `shape` as bare geometry in the document's coordinates — its path or
 * figure without any paint, placed by its transforms — for whoever wants to
 * draw something along it, such as the selection's outline.
 */
export function shapeSilhouetteOf(
    shape: Element | undefined,
    root: Element | null | undefined,
    geometry = shapeGeometryOf(shape, root),
): string | undefined {
    if (!shape || !geometry) return;

    const bare = bareGeometry(shape);
    const matrix = multiply(geometry.parentToRoot, geometry.transform);

    return `<g transform="${matrixAttribute(matrix)}">${new XMLSerializer().serializeToString(bare)}</g>`;
}

/**
 * How `shape` is painted, as the browser resolves it — attributes, classes,
 * a `<style>` and inline style alike — and how far an inset of the editor's
 * cuts it in. Colours come as `rgb(…)`, so that equal colours written
 * differently compare equal.
 */
export function shapeStyleOf(
    shape: Element | undefined,
): ShapeStyle | undefined {
    if (!shape) return;

    const computed = getComputedStyle(shape);
    const stroke = paint(computed.stroke);

    return {
        fill: paint(computed.fill) ?? "none",
        fillRule: computed.fillRule === "evenodd" ? "evenodd" : "nonzero",
        inset: insetOf(shape),
        stroke: stroke === "none" ? undefined : stroke,
        strokeWidth: Number.parseFloat(computed.strokeWidth) || 0,
    };
}

/**
 * How much path data `shape` holds — a path's `d`, a polygon's or
 * polyline's points, a little for the other figures — and how many
 * subpaths. What the outline costs grows with both.
 */
export function shapeWeightOf(shape: Element | undefined): OutlineWeight {
    const data = shape?.getAttribute("d");

    if (data !== null && data !== undefined) {
        return {
            characters: data.length,
            subpaths: data.match(/m/gi)?.length ?? 1,
        };
    }

    return {
        characters: shape?.getAttribute("points")?.length ?? FIGURE_WEIGHT,
        subpaths: 1,
    };
}

/**
 * Lets the shapes grow in one after another, by a delay per shape for the
 * document's appearing animation. A shape placed by a `transform` attribute
 * only fades in: the CSS animation sets `transform`, and the property wins
 * over the attribute of the same name — the shape would otherwise stay in
 * the wrong place for good.
 */
export function staggerAppearance(shapes: readonly Element[]) {
    const count = shapes.length;

    if (count === 0) return;

    const step =
        count > MAX_STAGGERED_SHAPES
            ? 0
            : Math.min(MAX_DELAY_STEP_MS, TOTAL_STAGGER_MS / count);

    for (const [index, shape] of shapes.entries()) {
        (shape as SVGElement).style.animationDelay =
            `${String(Math.round(index * step))}ms`;

        if (shape.hasAttribute("transform")) {
            shape.classList.add("svg-editor-shape--placed");
        }
    }
}

function fromDom(matrix: DOMMatrix): Matrix {
    return {
        a: matrix.a,
        b: matrix.b,
        c: matrix.c,
        d: matrix.d,
        e: matrix.e,
        f: matrix.f,
    };
}

function isGraphics(
    element: Element | null | undefined,
): element is SVGGraphicsElement {
    return (
        element !== null &&
        element !== undefined &&
        "getBBox" in element &&
        "getScreenCTM" in element
    );
}

/** The shape's own `transform` attribute, as one matrix. */
function ownTransform(shape: SVGGraphicsElement): Matrix {
    const list = shape.transform.baseVal;
    let matrix = IDENTITY;

    for (let index = 0; index < list.numberOfItems; index++) {
        matrix = multiply(matrix, fromDom(list.getItem(index).matrix));
    }

    return matrix;
}

/** A computed paint as a value to write back: `url("#id")` becomes `url(#id)`. */
function paint(value: string): string | undefined {
    const trimmed = value.trim();

    if (trimmed === "") return;

    return trimmed.replaceAll(/url\(["']([^"']+)["']\)/g, "url($1)");
}
